"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import { fetchFarcasterPreview } from "@/services/api/farcaster";
import type { FarcasterPreviewResponse, FarcasterCastPreview, FarcasterChannelPreview, FarcasterFramePreview, FarcasterProfilePreview, FarcasterUnavailablePreview, FarcasterUnsupportedPreview, FarcasterCastEmbed } from "@/types/farcaster.types";
import { LinkPreviewCardLayout } from "./OpenGraphPreview";
interface FarcasterCardProps {
  readonly href: string;
}
type SupportedPreview = Exclude<FarcasterPreviewResponse, FarcasterUnavailablePreview | FarcasterUnsupportedPreview>;
type FarcasterCardState = {
  readonly status: "loading";
} | {
  readonly status: "fallback";
} | {
  readonly status: "unavailable";
  readonly data: FarcasterUnavailablePreview;
} | {
  readonly status: "ready";
  readonly data: SupportedPreview;
};
const COMPACT_NUMBER_FORMATTER = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1
});
const RELATIVE_TIME_FORMATTER = new Intl.RelativeTimeFormat(undefined, {
  numeric: "auto"
});
const formatCount = (value: number | undefined): string | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  if (value < 1000) {
    return `${value}`;
  }
  return COMPACT_NUMBER_FORMATTER.format(value);
};
const formatRelativeTime = (timestamp: string | undefined): string | null => {
  if (!timestamp) {
    return null;
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const diffInSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [["year", 60 * 60 * 24 * 365], ["month", 60 * 60 * 24 * 30], ["week", 60 * 60 * 24 * 7], ["day", 60 * 60 * 24], ["hour", 60 * 60], ["minute", 60], ["second", 1]];
  for (const [unit, secondsInUnit] of units) {
    const value = Math.round(diffInSeconds / secondsInUnit);
    if (Math.abs(value) >= 1 || unit === "second") {
      return RELATIVE_TIME_FORMATTER.format(value, unit);
    }
  }
  return null;
};
const getPrimaryHref = (preview: {
  readonly canonicalUrl?: string | undefined;
}, fallback: string): string => {
  if (preview.canonicalUrl && preview.canonicalUrl.trim().length > 0) {
    return preview.canonicalUrl;
  }
  return fallback;
};
const ExternalLink = t0 => {
  const $ = _c(3);
  const {
    href,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== href) {
    t1 = <Link href={href} target="_blank" rel="noopener noreferrer" className="tw-inline-flex tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-border-solid tw-border-iron-600 tw-bg-iron-900/60 tw-px-3 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-iron-100 tw-no-underline tw-transition tw-duration-200 hover:tw-border-iron-400 hover:tw-text-white">{children}</Link>;
    $[0] = children;
    $[1] = href;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const renderImageGrid = (embeds: readonly FarcasterCastEmbed[] | undefined, authorName: string): ReactNode => {
  if (!embeds) {
    return null;
  }
  const images = embeds.filter(embed => embed.type === "image").map(embed => embed.previewImageUrl ?? embed.url).filter((url): url is string => typeof url === "string" && url.length > 0);
  if (images.length === 0) {
    return null;
  }
  let gridClass = "tw-grid tw-grid-cols-2 tw-gap-2";
  if (images.length === 1) {
    gridClass = "tw-flex";
  } else if (images.length === 3) {
    gridClass = "tw-grid tw-grid-cols-3 tw-gap-2";
  }
  return <div className={gridClass}>
      {images.slice(0, 4).map((url, index) => <div key={`${url}-${index}`} className="tw-relative tw-overflow-hidden tw-rounded-lg tw-bg-iron-800/60">
          <img src={url} alt={`Image from ${authorName}'s cast`} loading="lazy" decoding="async" className="tw-h-full tw-w-full tw-object-cover" />
        </div>)}
    </div>;
};
const CastHeader = t0 => {
  const $ = _c(23);
  const {
    preview
  } = t0;
  let t1;
  if ($[0] !== preview.timestamp) {
    t1 = formatRelativeTime(preview.timestamp);
    $[0] = preview.timestamp;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const timestamp = t1;
  let t2;
  if ($[2] !== preview.author.avatarUrl || $[3] !== preview.author.displayName) {
    t2 = preview.author.avatarUrl && <img src={preview.author.avatarUrl} alt={preview.author.displayName ? `${preview.author.displayName}'s avatar` : "Farcaster avatar"} loading="lazy" decoding="async" className="tw-h-12 tw-w-12 tw-flex-shrink-0 tw-rounded-full tw-object-cover" />;
    $[2] = preview.author.avatarUrl;
    $[3] = preview.author.displayName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = preview.author.displayName ?? preview.author.username ?? "Farcaster user";
  let t4;
  if ($[5] !== t3) {
    t4 = <span className="tw-text-base tw-font-semibold tw-text-iron-100">{t3}</span>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== preview.author.username) {
    t5 = preview.author.username && <span className="tw-text-sm tw-text-iron-400">@{preview.author.username}</span>;
    $[7] = preview.author.username;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== timestamp) {
    t6 = timestamp && <span className="tw-text-xs tw-text-iron-500">{timestamp}</span>;
    $[9] = timestamp;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t4}{t5}{t6}</div>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== preview.channel) {
    t8 = preview.channel?.name && <div className="tw-mt-2 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-iron-300">{preview.channel.iconUrl && <img src={preview.channel.iconUrl} alt={`${preview.channel.name} channel icon`} loading="lazy" decoding="async" className="tw-h-4 tw-w-4 tw-rounded-full tw-object-cover" />}<span>/{preview.channel.name}</span></div>;
    $[15] = preview.channel;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <div className="tw-min-w-0 tw-flex-1">{t7}{t8}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t2 || $[21] !== t9) {
    t10 = <div className="tw-flex tw-items-start tw-gap-3">{t2}{t9}</div>;
    $[20] = t2;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};
const renderReactions = (reactions: FarcasterCastPreview["cast"]["reactions"] | null | undefined): ReactNode => {
  if (!reactions) {
    return null;
  }
  const stats: Array<{
    label: string;
    value: string | null;
  }> = [{
    label: "Likes",
    value: formatCount(reactions.likes)
  }, {
    label: "Recasts",
    value: formatCount(reactions.recasts)
  }, {
    label: "Replies",
    value: formatCount(reactions.replies)
  }];
  const visible = stats.filter(stat => stat.value !== null);
  if (visible.length === 0) {
    return null;
  }
  return <div className="tw-flex tw-flex-wrap tw-gap-4">
      {visible.map(stat => <div key={stat.label} className="tw-text-sm tw-text-iron-300">
          <span className="tw-font-semibold tw-text-iron-100">{stat.value}</span>
          <span className="tw-ml-1">{stat.label}</span>
        </div>)}
    </div>;
};
const CastBody = t0 => {
  const $ = _c(14);
  const {
    preview
  } = t0;
  const authorName = preview.cast.author.displayName ?? preview.cast.author.username ?? "Farcaster";
  let t1;
  if ($[0] !== preview.cast) {
    t1 = <CastHeader preview={preview.cast} />;
    $[0] = preview.cast;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== preview.cast.text) {
    t2 = preview.cast.text && <p className="tw-m-0 tw-text-sm tw-leading-6 tw-text-iron-200 tw-whitespace-pre-wrap tw-break-words">{preview.cast.text}</p>;
    $[2] = preview.cast.text;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== authorName || $[5] !== preview.cast.embeds) {
    t3 = renderImageGrid(preview.cast.embeds, authorName);
    $[4] = authorName;
    $[5] = preview.cast.embeds;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== preview.cast.reactions) {
    t4 = renderReactions(preview.cast.reactions);
    $[7] = preview.cast.reactions;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <div className="tw-space-y-4">{t1}{t2}{t3}{t4}</div>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
const ProfileBody = t0 => {
  const $ = _c(18);
  const {
    preview
  } = t0;
  let t1;
  if ($[0] !== preview.profile.avatarUrl || $[1] !== preview.profile.displayName) {
    t1 = preview.profile.avatarUrl && <img src={preview.profile.avatarUrl} alt={preview.profile.displayName ? `${preview.profile.displayName}'s avatar` : "Farcaster avatar"} loading="lazy" decoding="async" className="tw-h-16 tw-w-16 tw-flex-shrink-0 tw-rounded-full tw-object-cover" />;
    $[0] = preview.profile.avatarUrl;
    $[1] = preview.profile.displayName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = preview.profile.displayName ?? preview.profile.username ?? "Farcaster user";
  let t3;
  if ($[3] !== t2) {
    t3 = <p className="tw-m-0 tw-text-lg tw-font-semibold tw-text-iron-100">{t2}</p>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== preview.profile.username) {
    t4 = preview.profile.username && <p className="tw-m-0 tw-text-sm tw-text-iron-400">@{preview.profile.username}</p>;
    $[5] = preview.profile.username;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className="tw-space-y-1">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== preview.profile.bio) {
    t6 = preview.profile.bio && <p className="tw-m-0 tw-text-sm tw-text-iron-200 tw-whitespace-pre-wrap tw-break-words">{preview.profile.bio}</p>;
    $[10] = preview.profile.bio;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="tw-min-w-0 tw-space-y-2">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t1 || $[16] !== t7) {
    t8 = <div className="tw-flex tw-gap-3">{t1}{t7}</div>;
    $[15] = t1;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
};
const ChannelBody = t0 => {
  const $ = _c(21);
  const {
    preview
  } = t0;
  let t1;
  if ($[0] !== preview.channel.iconUrl || $[1] !== preview.channel.name) {
    t1 = preview.channel.iconUrl && <img src={preview.channel.iconUrl} alt={preview.channel.name ? `${preview.channel.name} icon` : "Channel icon"} loading="lazy" decoding="async" className="tw-h-12 tw-w-12 tw-rounded-full tw-object-cover" />;
    $[0] = preview.channel.iconUrl;
    $[1] = preview.channel.name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = preview.channel.name ?? `/${preview.channel.id ?? "channel"}`;
  let t3;
  if ($[3] !== t2) {
    t3 = <p className="tw-m-0 tw-text-base tw-font-semibold tw-text-iron-100">{t2}</p>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== preview.channel.id) {
    t4 = preview.channel.id && <p className="tw-m-0 tw-text-xs tw-uppercase tw-tracking-wide tw-text-iron-400">/{preview.channel.id}</p>;
    $[5] = preview.channel.id;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className="tw-min-w-0">{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t1 || $[11] !== t5) {
    t6 = <div className="tw-flex tw-items-center tw-gap-3">{t1}{t5}</div>;
    $[10] = t1;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== preview.channel.description) {
    t7 = preview.channel.description && <p className="tw-m-0 tw-text-sm tw-text-iron-200 tw-whitespace-pre-wrap tw-break-words">{preview.channel.description}</p>;
    $[13] = preview.channel.description;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== preview.channel.latestCast) {
    t8 = preview.channel.latestCast?.text && <div className="tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/30 tw-p-3"><p className="tw-m-0 tw-text-xs tw-uppercase tw-tracking-wide tw-text-iron-400">Latest cast</p><p className="tw-mt-2 tw-text-sm tw-text-iron-100 tw-whitespace-pre-wrap tw-break-words">{preview.channel.latestCast.text}</p>{preview.channel.latestCast.author && <p className="tw-m-0 tw-text-xs tw-text-iron-400">@{preview.channel.latestCast.author}</p>}</div>;
    $[15] = preview.channel.latestCast;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t6 || $[18] !== t7 || $[19] !== t8) {
    t9 = <div className="tw-space-y-3">{t6}{t7}{t8}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
const FrameBody = t0 => {
  const $ = _c(27);
  const {
    preview
  } = t0;
  let t1;
  if ($[0] !== preview.frame.imageUrl || $[1] !== preview.frame.title) {
    t1 = preview.frame.imageUrl && <div className="tw-overflow-hidden tw-rounded-xl tw-bg-iron-900/40"><img src={preview.frame.imageUrl} alt={preview.frame.title ?? "Frame preview"} loading="lazy" decoding="async" className="tw-h-full tw-w-full tw-object-cover" /></div>;
    $[0] = preview.frame.imageUrl;
    $[1] = preview.frame.title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== preview.frame.title) {
    t2 = preview.frame.title && <p className="tw-m-0 tw-text-lg tw-font-semibold tw-text-iron-100">{preview.frame.title}</p>;
    $[3] = preview.frame.title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== preview.frame.frameUrl || $[6] !== preview.frame.siteName) {
    t3 = preview.frame.siteName ?? new URL(preview.frame.frameUrl).host;
    $[5] = preview.frame.frameUrl;
    $[6] = preview.frame.siteName;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <p className="tw-m-0 tw-text-sm tw-text-iron-400">{t3}</p>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t4) {
    t5 = <div className="tw-space-y-1">{t2}{t4}</div>;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== preview.frame.buttons) {
    t6 = preview.frame.buttons && preview.frame.buttons.length > 0 && <div className="tw-flex tw-flex-wrap tw-gap-2">{preview.frame.buttons.map(_temp)}</div>;
    $[13] = preview.frame.buttons;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = preview.frame.castUrl ?? preview.frame.frameUrl;
  let t8;
  if ($[15] !== t7) {
    t8 = <ExternalLink href={t7}>Open in Warpcast</ExternalLink>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== preview.frame.frameUrl) {
    t9 = <ExternalLink href={preview.frame.frameUrl}>Open frame URL</ExternalLink>;
    $[17] = preview.frame.frameUrl;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <div className="tw-flex tw-flex-wrap tw-gap-3">{t8}{t9}</div>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t1 || $[23] !== t10 || $[24] !== t5 || $[25] !== t6) {
    t11 = <div className="tw-space-y-4">{t1}{t5}{t6}{t10}</div>;
    $[22] = t1;
    $[23] = t10;
    $[24] = t5;
    $[25] = t6;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};
const renderContent = (preview: SupportedPreview, href: string): ReactElement => {
  if (preview.type === "cast") {
    const primaryHref = getPrimaryHref(preview, href);
    return <LinkPreviewCardLayout href={href}>
        <div className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4 tw-space-y-4">
          <CastBody preview={preview} />
          <div className="tw-flex tw-flex-wrap tw-gap-3">
            <ExternalLink href={primaryHref}>Open on Warpcast</ExternalLink>
          </div>
        </div>
      </LinkPreviewCardLayout>;
  }
  if (preview.type === "profile") {
    const primaryHref = getPrimaryHref(preview, href);
    return <LinkPreviewCardLayout href={href}>
        <div className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4 tw-space-y-4">
          <ProfileBody preview={preview} />
          <ExternalLink href={primaryHref}>Open profile on Warpcast</ExternalLink>
        </div>
      </LinkPreviewCardLayout>;
  }
  if (preview.type === "channel") {
    const primaryHref = getPrimaryHref(preview, href);
    return <LinkPreviewCardLayout href={href}>
        <div className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4 tw-space-y-4">
          <ChannelBody preview={preview} />
          <ExternalLink href={primaryHref}>Open channel on Warpcast</ExternalLink>
        </div>
      </LinkPreviewCardLayout>;
  }
  if (preview.type === "frame") {
    return <LinkPreviewCardLayout href={href}>
        <div className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4">
          <FrameBody preview={preview} />
        </div>
      </LinkPreviewCardLayout>;
  }
  return <LinkPreviewCardLayout href={href}>
      <div className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4">
        <p className="tw-m-0 tw-text-sm tw-text-iron-200">Unsupported preview.</p>
      </div>
    </LinkPreviewCardLayout>;
};
const renderUnavailable = (preview: FarcasterUnavailablePreview, href: string): ReactElement => {
  const primaryHref = getPrimaryHref(preview, href);
  return <LinkPreviewCardLayout href={href}>
      <div className="tw-flex tw-flex-col tw-items-start tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4">
        <div>
          <p className="tw-m-0 tw-text-base tw-font-semibold tw-text-iron-100">
            Unavailable on Farcaster
          </p>
          {preview.reason && <p className="tw-mt-1 tw-text-sm tw-text-iron-300">{preview.reason}</p>}
        </div>
        <ExternalLink href={primaryHref}>Open on Warpcast</ExternalLink>
      </div>
    </LinkPreviewCardLayout>;
};
export default function FarcasterCard(t0) {
  const $ = _c(14);
  const {
    href
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      status: "loading"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [state, setState] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== href) {
    t2 = () => {
      let isActive = true;
      setState({
        status: "loading"
      });
      fetchFarcasterPreview(href).then(response => {
        if (!isActive) {
          return;
        }
        if (!response || response.type === "unsupported") {
          setState({
            status: "fallback"
          });
          return;
        }
        if (response.type === "unavailable") {
          setState({
            status: "unavailable",
            data: response
          });
          return;
        }
        setState({
          status: "ready",
          data: response
        });
      }).catch(() => {
        if (isActive) {
          setState({
            status: "fallback"
          });
        }
      });
      return () => {
        isActive = false;
      };
    };
    t3 = [href];
    $[1] = href;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  if (state.status === "fallback") {
    throw new Error("Unsupported Farcaster preview");
  }
  if (state.status === "loading") {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="tw-h-12 tw-w-12 tw-rounded-full tw-bg-iron-800/60" />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4"><div className="tw-animate-pulse tw-space-y-3"><div className="tw-flex tw-items-center tw-gap-3">{t4}<div className="tw-flex-1 tw-space-y-2"><div className="tw-h-4 tw-w-1/3 tw-rounded tw-bg-iron-800/60" /><div className="tw-h-3 tw-w-1/4 tw-rounded tw-bg-iron-800/40" /></div></div><div className="tw-h-3 tw-w-full tw-rounded tw-bg-iron-800/40" /><div className="tw-h-3 tw-w-2/3 tw-rounded tw-bg-iron-800/30" /><div className="tw-h-40 tw-w-full tw-rounded-xl tw-bg-iron-800/30" /></div></div>;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== href) {
      t6 = <LinkPreviewCardLayout href={href}>{t5}</LinkPreviewCardLayout>;
      $[6] = href;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    return t6;
  }
  if (state.status === "unavailable") {
    let t4;
    if ($[8] !== href || $[9] !== state.data) {
      t4 = renderUnavailable(state.data, href);
      $[8] = href;
      $[9] = state.data;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  let t4;
  if ($[11] !== href || $[12] !== state.data) {
    t4 = renderContent(state.data, href);
    $[11] = href;
    $[12] = state.data;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function _temp(label, index) {
  return <span key={`${label}-${index}`} className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-iron-800/60 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-text-iron-200">{label}</span>;
}